"use client"

import { createContext, useContext, useState, useEffect, useCallback } from "react"
// import axios from "../../utils/axiosAuth"
import { BASE_LOGIN_URL } from "../../config"
import { axiosAuth } from "../../services/axiosAuth"
import { useAuthStore } from "../hooks/useAuth"

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userData, setUserData] = useState(null)
  const [userDataLoading, setUserDataLoading] = useState(false)
    const { token, setUser:setUserZustand } = useAuthStore();

  // Centralized token verification function
  const verifyToken = useCallback(async () => {
    try {
      const response = await axiosAuth.get("/auth/verifytoken")

      if (response.data && response.data.user) {
        setUser(response.data.user)
        setUserZustand(response.data.user);
        setIsAuthenticated(true)
        return response.data.user
      } else {
        throw new Error("Invalid user data")
      }
    } catch (error) {
      console.warn("❌ Token verification failed:", error.response?.data?.msg || error.message)
      setUser(null)
      setIsAuthenticated(false)
      throw error
    }
  }, [])

  const silentVerifyToken = useCallback(async () => {
    try {
      await verifyToken()
    } catch (error) {
      if (error.response?.status === 401) {
        console.log("Silent token verification failed, redirecting to login...")
        logout()
      }
    }
  }, [verifyToken])

  const fetchUserData = useCallback(async () => {
    if (!isAuthenticated) return null

    setUserDataLoading(true)
    try {
      const response = await axiosAuth.get("/user")
      if (response.data) {
        console.log("response.data", response.data);
        setUserData(response.data)
        return response.data
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error)
      // If 401, user session expired
      if (error.response?.status === 401) {
        logout()
      }
    } finally {
      setUserDataLoading(false)
    }
    return null
  }, [isAuthenticated])

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await verifyToken()
      } catch (error) {
        // Check if this is a 401 error (token expired/invalid)
        if (error.response?.status === 401) {
          console.log("Authentication failed during initialization, redirecting to login...")
          logout()
          return
        }
        // For other errors (network issues, etc.), just log and continue
        console.log("User not authenticated, will redirect if accessing protected routes")
      } finally {
        setLoading(false)
      }
    }

    initializeAuth()
  }, [verifyToken])

  useEffect(() => {
    if (isAuthenticated && user && !userData) {
      fetchUserData()
    }
  }, [isAuthenticated, user, userData, fetchUserData])

  // Logout function
  const logout = useCallback(() => {
    setUser(null)
    setIsAuthenticated(false)
    setUserData(null)
    const currentUrl = window.location.href
    const encodedRedirect = encodeURIComponent(currentUrl)
    window.location.href = `${BASE_LOGIN_URL}?redirectTo=${encodedRedirect}`
  }, [])

  // Refresh user data (useful after profile updates)
  const refreshUser = useCallback(async () => {
    try {
      const authUser = await verifyToken()
      const detailedUser = await fetchUserData()
      return { authUser, detailedUser }
    } catch (error) {
      if (error.response?.status === 401) {
        logout()
      }
      throw error
    }
  }, [verifyToken, fetchUserData, logout])

  const getUserPlanType = useCallback(() => {
    return userData?.planType || "free_plan"
  }, [userData])

  const isPaidUser = useCallback(() => {
    return getUserPlanType() === "paid_plan"
  }, [getUserPlanType])

  const value = {
    user,
    loading,
    isAuthenticated,
    verifyToken,
    logout,
    refreshUser,
    userData,
    userDataLoading,
    fetchUserData,
    getUserPlanType,
    isPaidUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
